import {
  CashFlowCollection,
  CashFlowsByNature,
  GroupedCashFlow,
  GroupedCashFlows,
  Transaction,
  AnalysisCalendar,
} from '@algoan/dashboard/model';
import {
  extractYearAndMonth,
  isRejectedTransaction,
  selectDate,
  sumTransactionsAmount,
  isCashFlowCollection,
} from '@algoan/dashboard/utils';

export const sumCategoriesForGivenMonth = (
  groupedCashFlows: GroupedCashFlows,
  checkedCashFlows: { [key: number]: boolean },
  month: string,
): {
  checkedFixedCharges: number;
  checkedResources: number;
  checkedIncomes: number;
  checkedExpenses: number;
  checkedLoans: number;
  uncheckedLoans: number;
} => {
  const { incomes, expenses, loans, fixedCharges, resources } = groupedCashFlows;

  const uncheckedLoans = loans.cashFlows
    .filter((cashFlow) => !checkedCashFlows[cashFlow.index])
    .reduce((total, cashFlow) => total + (cashFlow.calendar[month]?.amount ?? 0), 0);

  return {
    checkedFixedCharges: sumSelectedTransactionsForGivenMonth(fixedCharges, month, checkedCashFlows),
    checkedResources: sumSelectedTransactionsForGivenMonth(resources, month, checkedCashFlows),
    checkedIncomes: sumSelectedTransactionsForGivenMonth(incomes, month, checkedCashFlows),
    checkedExpenses: sumSelectedTransactionsForGivenMonth(expenses, month, checkedCashFlows),
    checkedLoans: sumSelectedTransactionsForGivenMonth(loans, month, checkedCashFlows),
    uncheckedLoans,
  };
};

export const sumCategoriesTotal = (
  groupedCashFlows: GroupedCashFlows,
  checkedCashFlows: { [key: number]: boolean },
): {
  checkedFixedCharges: number;
  checkedResources: number;
  checkedIncomes: number;
  checkedExpenses: number;
  checkedLoans: number;
  uncheckedLoans: number;
} => {
  const { incomes, expenses, loans, fixedCharges, resources } = groupedCashFlows;

  const uncheckedLoans = loans.cashFlows
    .filter((cashFlow) => !checkedCashFlows[cashFlow.index])
    .reduce((total, cashFlow) => total + sumTotalForCashFlow(cashFlow), 0);

  return {
    checkedFixedCharges: sumSelectedTransactionsTotal(fixedCharges, checkedCashFlows),
    checkedResources: sumSelectedTransactionsTotal(resources, checkedCashFlows),
    checkedIncomes: sumSelectedTransactionsTotal(incomes, checkedCashFlows),
    checkedExpenses: sumSelectedTransactionsTotal(expenses, checkedCashFlows),
    checkedLoans: sumSelectedTransactionsTotal(loans, checkedCashFlows),
    uncheckedLoans: uncheckedLoans,
  };
};

export const calculateLastFullCompleteMonth = (groupedCashFlows: GroupedCashFlows): GroupedCashFlows['calendar'][0] => {
  // Slice to make a copy of the object
  const reversedCalendar = groupedCashFlows.calendar.slice().reverse();

  const getDaysInMonth = (month: number, year: number): number => new Date(year, month, 0).getDate();

  const lastMonthInCalendar = parseInt(reversedCalendar[0].month.split('-')[1]);

  const lastFullCompleteMonth = reversedCalendar.find((month) => {
    const yearComponent = parseInt(month.month.split('-')[0]);
    const monthComponent = parseInt(month.month.split('-')[1]);

    return month.nbDays === getDaysInMonth(monthComponent, yearComponent) && monthComponent !== lastMonthInCalendar;
  });

  // if last full complete month does not exist, return the current month
  return lastFullCompleteMonth ?? reversedCalendar[0];
};

const sumSelectedTransactionsForGivenMonth = (
  cashFlowsByNature: CashFlowsByNature,
  month: string,
  checkedCashFlows: { [key: number]: boolean },
): number => {
  return getEveryCashFlowsFromCashFlowCollection(cashFlowsByNature.cashFlows)
    .filter((cashFlow) => {
      const isInDate = cashFlow.calendar[month];
      const isChecked = checkedCashFlows[cashFlow.index];
      return isInDate && isChecked;
    })
    .reduce((total, cashFlow) => total + (cashFlow.calendar[month]?.amount || 0), 0);
};

const sumSelectedTransactionsTotal = (
  cashFlowsByNature: CashFlowsByNature,
  checkedCashFlows: { [key: number]: boolean },
): number => {
  return getEveryCashFlowsFromCashFlowCollection(cashFlowsByNature.cashFlows)
    .filter((cashFlow) => checkedCashFlows[cashFlow.index])
    .reduce((total, cashFlow) => total + sumTotalForCashFlow(cashFlow), 0);
};

export const calculateCashFlow = ({
  resources,
  incomes,
  fixedCharges,
  expenses,
  loans,
}: {
  resources: number;
  incomes: number;
  fixedCharges: number;
  expenses: number;
  loans: number;
}): number => {
  return resources + incomes - Math.abs(fixedCharges) - Math.abs(expenses) - Math.abs(loans);
};

// TABLE CALCULATIONS

export const sumCategoryForMonth = (
  date: AnalysisCalendar,
  cashFlows: GroupedCashFlow[],
  checkedRows: { [key: number]: boolean },
): number =>
  cashFlows
    .filter((cashFlow) => checkedRows[cashFlow.index])
    .reduce((accumulator, cashFlow) => Math.abs(cashFlow.calendar[date.month]?.amount || 0) + Math.abs(accumulator), 0);

export const sumTotalForCategory = (cashFlows: GroupedCashFlow[], checkedRows: { [key: number]: boolean }): number =>
  cashFlows
    .filter((cashFlow) => checkedRows[cashFlow.index])
    .reduce((accumulator, cashFlow) => sumTotalForCashFlow(cashFlow) + accumulator, 0);

export const sumTotalForCashFlow = (cashFlow: GroupedCashFlow): number => {
  const months = Object.keys(cashFlow.calendar);
  return months.reduce(
    (accumulator, month) => Math.abs(cashFlow.calendar[month]?.amount || 0) + Math.abs(accumulator),
    0,
  );
};

export const getEveryCashFlowsFromCashFlowCollection = (
  cashFlows: Array<GroupedCashFlow | CashFlowCollection>,
): GroupedCashFlow[] =>
  cashFlows.reduce<GroupedCashFlow[]>((cashFlows, cashFlowOrCashFlowCollection) => {
    if (isCashFlowCollection(cashFlowOrCashFlowCollection)) {
      cashFlows.push(...cashFlowOrCashFlowCollection.cashFlows);

      return cashFlows;
    }

    cashFlows.push(cashFlowOrCashFlowCollection);

    return cashFlows;
  }, []);

export const getEveryCashFlowsFromAnalysis = (
  groupedCashFlows?: GroupedCashFlows,
): Array<GroupedCashFlow | CashFlowCollection> =>
  groupedCashFlows !== undefined
    ? groupedCashFlows.incomes.cashFlows
        .concat(groupedCashFlows.expenses.cashFlows)
        .concat(groupedCashFlows.loans.cashFlows)
        .concat(groupedCashFlows.fixedCharges.cashFlows)
        .concat(groupedCashFlows.resources.cashFlows)
    : [];

export const hasCashFlowCollection = (groupedCashFlows?: GroupedCashFlows): boolean =>
  getEveryCashFlowsFromAnalysis(groupedCashFlows).some(isCashFlowCollection);

export const filterTransactionsForAMonth = (transactions: Transaction[], month: string): Transaction[] => {
  return transactions.filter((transaction) => extractYearAndMonth(selectDate(transaction.dates)) === month);
};

export const getTotalAmountOfRejectedPaymentsForAMonth = (transactions: Transaction[], month: string): number => {
  const rejectedTransactions = filterTransactionsForAMonth(transactions, month).filter((transaction) =>
    isRejectedTransaction(transaction),
  );

  return Math.abs(sumTransactionsAmount(rejectedTransactions));
};
